import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  useToast,
  Text,
  Image,
  Link as ChakraLink,
  HStack,
  Divider,
  Icon,
  Flex,
  Textarea,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl as ChakraFormControl,
  Select as ChakraSelect,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Spinner,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { FaLinkedin } from 'react-icons/fa';
import { supabase } from './supabaseClient';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { FaFileInvoice, FaExclamationCircle, FaHourglassHalf, FaRegPaperPlane, FaRegClock } from 'react-icons/fa';
import { EditIcon } from '@chakra-ui/icons';

function InvoiceUpload() {
  const [file, setFile] = useState(null);
  const [associateFirstName, setAssociateFirstName] = useState('');
  const [associateLastName, setAssociateLastName] = useState('');
  const [clientName, setClientName] = useState('');
  const [wantsEmail, setWantsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const [associateComments, setAssociateComments] = useState('');

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
      } else {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!associateFirstName.trim()) {
      toast({
        title: 'First Name required',
        description: 'Please enter your first name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!associateLastName.trim()) {
      toast({
        title: 'Last Name required',
        description: 'Please enter your last name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!clientName.trim()) {
      toast({
        title: 'Client Name required',
        description: 'Please enter the client name you are assigned to',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select an invoice to upload',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (wantsEmail && !email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('associate_first_name', associateFirstName);
      formData.append('associate_last_name', associateLastName);
      formData.append('client_name', clientName);
      formData.append('file', file);
      formData.append('email', wantsEmail ? email : 'N/A');
      formData.append('associate_comments', associateComments);
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const date = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
      const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
      formData.append('date', date);
      formData.append('time', time);
      const response = await fetch('https://n8n.alba-nova.com/webhook/invoice-upload', {
        method: 'POST',
        headers: {
          'x-api-key': 'brighter-invoice-upload-n8n',
        },
        body: formData,
      });
      if (!response.ok) {
        await response.text();
        throw new Error(`Upload failed with status ${response.status}`);
      }
      setFile(null);
      setAssociateFirstName('');
      setAssociateLastName('');
      setClientName('');
      setEmail('');
      setWantsEmail(false);
      setAssociateComments('');
      e.target.reset();
      navigate('/success');
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center" mb={4}>
          <a href="https://brighterconsultancy.com/" target="_blank" rel="noopener noreferrer">
            <Image
              src="/brighter_logo.png"
              alt="Brighter Logo"
              maxH="120px"
              mx="auto"
              mb={6}
            />
          </a>
        </Box>
        <Box
          as="form"
          onSubmit={handleSubmit}
          p={8}
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="lg"
          bg="white"
        >
          <VStack spacing={6} align="stretch">
            <HStack spacing={4} align="flex-end">
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Associate First Name</FormLabel>
                <Input
                  type="text"
                  value={associateFirstName}
                  onChange={(e) => setAssociateFirstName(e.target.value)}
                  focusBorderColor="green.400"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Associate Last Name</FormLabel>
                <Input
                  type="text"
                  value={associateLastName}
                  onChange={(e) => setAssociateLastName(e.target.value)}
                  focusBorderColor="green.400"
                />
              </FormControl>
            </HStack>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">End Client Name</FormLabel>
              <Input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Name of end client you are assigned to (e.g. Unilever)"
                focusBorderColor="green.400"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Upload Invoice</FormLabel>
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                p={3}
                h="auto"
                sx={{
                  '::file-selector-button': {
                    border: 'none',
                    outline: 'none',
                    mr: 2,
                    p: '8px 16px',
                    background: 'gray.100',
                    borderRadius: 'md',
                    color: 'gray.700',
                    fontSize: 'sm',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    _hover: {
                      background: 'gray.200',
                    },
                  },
                }}
              />
              {file && (
                <Text fontSize="sm" color="green.600" mt={2}>
                  Selected file: {file.name}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="bold">Additional Comments</FormLabel>
              <Textarea
                value={associateComments}
                onChange={(e) => setAssociateComments(e.target.value)}
                rows={2}
                resize="vertical"
                focusBorderColor="green.400"
              />
            </FormControl>
            <FormControl>
              <Checkbox
                isChecked={wantsEmail}
                onChange={(e) => setWantsEmail(e.target.checked)}
                colorScheme="green"
              >
                Receive confirmation email
              </Checkbox>
            </FormControl>
            {wantsEmail && (
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  focusBorderColor="green.400"
                />
              </FormControl>
            )}
            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              width="full"
              mt={4}
              _hover={{ bg: 'green.500' }}
              isLoading={isSubmitting}
              loadingText="Uploading..."
            >
              Upload Invoice
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}

function InvoiceUploadSandbox() {
  const [file, setFile] = useState(null);
  const [associateFirstName, setAssociateFirstName] = useState('');
  const [associateLastName, setAssociateLastName] = useState('');
  const [clientName, setClientName] = useState('');
  const [wantsEmail, setWantsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [associateComments, setAssociateComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
      } else {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PDF file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!associateFirstName.trim()) {
      toast({
        title: 'First Name required',
        description: 'Please enter your first name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!associateLastName.trim()) {
      toast({
        title: 'Last Name required',
        description: 'Please enter your last name',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!clientName.trim()) {
      toast({
        title: 'Client Name required',
        description: 'Please enter the client name you are assigned to',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select an invoice to upload',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (wantsEmail && !email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('associate_first_name', associateFirstName);
      formData.append('associate_last_name', associateLastName);
      formData.append('client_name', clientName);
      formData.append('file', file);
      formData.append('email', wantsEmail ? email : 'N/A');
      formData.append('associate_comments', associateComments);
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const date = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
      const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
      formData.append('date', date);
      formData.append('time', time);
      const response = await fetch('https://n8n.alba-nova.com/webhook-test/7ca10d38-dffd-4043-ab7b-1dae4c4797ff', {
        method: 'POST',
        headers: {
          'x-api-key': 'brighter-invoice-upload-n8n',
        },
        body: formData,
      });
      if (!response.ok) {
        await response.text();
        throw new Error(`Upload failed with status ${response.status}`);
      }
      setFile(null);
      setAssociateFirstName('');
      setAssociateLastName('');
      setClientName('');
      setEmail('');
      setWantsEmail(false);
      setAssociateComments('');
      e.target.reset();
      navigate('/success');
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={8} align="stretch">
        <Box bg="yellow.300" color="black" textAlign="center" fontWeight="bold" py={2} borderRadius="md" mb={2} position="sticky" top="0" zIndex="1000">
          THIS IS THE SANDBOX ENVIRONMENT FOR TESTING
        </Box>
        <Box textAlign="center" mb={4}>
          <a href="https://brighterconsultancy.com/" target="_blank" rel="noopener noreferrer">
            <Image
              src="/brighter_logo.png"
              alt="Brighter Logo"
              maxH="120px"
              mx="auto"
              mb={6}
            />
          </a>
        </Box>
        <Box
          as="form"
          onSubmit={handleSubmit}
          p={8}
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="lg"
          bg="white"
        >
          <VStack spacing={6} align="stretch">
            <HStack spacing={4} align="flex-end">
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Associate First Name</FormLabel>
                <Input
                  type="text"
                  value={associateFirstName}
                  onChange={(e) => setAssociateFirstName(e.target.value)}
                  focusBorderColor="green.400"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Associate Last Name</FormLabel>
                <Input
                  type="text"
                  value={associateLastName}
                  onChange={(e) => setAssociateLastName(e.target.value)}
                  focusBorderColor="green.400"
                />
              </FormControl>
            </HStack>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Client Name</FormLabel>
              <Input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Name of end client you are assigned to (e.g. Unilever)"
                focusBorderColor="green.400"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontWeight="bold">Upload Invoice</FormLabel>
              <Input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                p={3}
                h="auto"
                sx={{
                  '::file-selector-button': {
                    border: 'none',
                    outline: 'none',
                    mr: 2,
                    p: '8px 16px',
                    background: 'gray.100',
                    borderRadius: 'md',
                    color: 'gray.700',
                    fontSize: 'sm',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    _hover: {
                      background: 'gray.200',
                    },
                  },
                }}
              />
              {file && (
                <Text fontSize="sm" color="green.600" mt={2}>
                  Selected file: {file.name}
                </Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel fontWeight="bold">Additional Comments</FormLabel>
              <Textarea
                value={associateComments}
                onChange={(e) => setAssociateComments(e.target.value)}
                rows={2}
                resize="vertical"
                focusBorderColor="green.400"
              />
            </FormControl>
            <FormControl>
              <Checkbox
                isChecked={wantsEmail}
                onChange={(e) => setWantsEmail(e.target.checked)}
                colorScheme="green"
              >
                Receive confirmation email
              </Checkbox>
            </FormControl>
            {wantsEmail && (
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Email Address</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  focusBorderColor="green.400"
                />
              </FormControl>
            )}
            <Button
              type="submit"
              colorScheme="green"
              size="lg"
              width="full"
              mt={4}
              _hover={{ bg: 'green.500' }}
              isLoading={isSubmitting}
              loadingText="Uploading..."
            >
              Upload Invoice
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}

function SuccessPage() {
  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center" mb={4}>
          <a href="https://brighterconsultancy.com/" target="_blank" rel="noopener noreferrer">
            <Image
              src="/brighter_logo.png"
              alt="Brighter Logo"
              maxH="120px"
              mx="auto"
              mb={6}
            />
          </a>
        </Box>
        <Box
          p={8}
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="lg"
          bg="white"
          textAlign="center"
        >
          <Text fontSize="2xl" fontWeight="bold" mb={4}>
            Thank you for uploading your invoice.
          </Text>
          <Button
            colorScheme="green"
            size="lg"
            mt={4}
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Upload Another Invoice
          </Button>
        </Box>
      </VStack>
    </Container>
  );
}

function BrighterLogoHeader() {
  return (
    <Box textAlign="center" mb={4}>
      <a href="https://brighterconsultancy.com/" target="_blank" rel="noopener noreferrer">
        <Image
          src="/brighter_logo.png"
          alt="Brighter Logo"
          maxH="120px"
          mx="auto"
          mb={6}
        />
      </a>
    </Box>
  );
}

function UploadedInvoiceDetailsModal({ isOpen, onClose, invoice, onSave }) {
  // Editable fields
  const [invoiceStatus, setInvoiceStatus] = useState(invoice.invoiceStatus || 'Pending');
  const [clientInvoiceNo, setClientInvoiceNo] = useState(invoice.clientInvoiceNo || '');
  const [internalComments, setInternalComments] = useState(invoice.internalComments || '');
  const [editingStatus, setEditingStatus] = useState(false);

  // Calculated fields
  const quantity = invoice.quantity || 20;
  const dayRate = invoice.dayRate || 650;
  const clientCharge = invoice.clientCharge || 800;
  const totalWithoutVAT = quantity * dayRate;
  const vat = Math.round(totalWithoutVAT * 0.2 * 100) / 100;
  const totalWithVAT = totalWithoutVAT + vat;
  const marginPerDay = clientCharge - dayRate;
  const clientInvoiceValue = quantity * clientCharge;
  const recruitmentCost = Math.round(clientCharge * 0.1 * 100) / 100;
  const dealMargin = invoice.dealMargin || 'OJ-Legacy';
  const totalMargin = quantity * marginPerDay;
  const ojMargin = Math.round(recruitmentCost * quantity * 100) / 100;
  const brighterMargin = totalMargin - ojMargin;
  const brighterProfit = (clientCharge - dayRate - recruitmentCost) * quantity;
  const ojMarginPercent = Math.round((recruitmentCost / dayRate) * 10000) / 100;

  // Dummy fields for now
  const invoiceDate = invoice.invoiceDate || '10-06-2024';
  const invoiceNumber = invoice.invoiceNumber || 'INV-2024-001';
  const currency = invoice.currency || 'GBP';
  const timeSubmitted = invoice.timeSubmitted || '14:32';
  const projectCode = invoice.projectCode || '3043a';
  const projectEndDate = invoice.projectEndDate || '30-06-2024';
  const clientName = invoice.clientName || 'Talk Talk Communications Limited';
  const associates = invoice.associates || 1;
  const comments = invoice.comments || 'No issues found.';
  const invoiceLink = invoice.invoiceUrl || '#';
  const startDate = invoice.startDate || '01-06-2024';
  // CMAP dummy fields
  const cmapCompanyId = invoice.cmapCompanyId || '789957';
  const cmapProjectId = invoice.cmapProjectId || '1641387';
  const cmapCompanyLink = invoice.cmapCompanyLink || '#';
  const cmapProjectLink = invoice.cmapProjectLink || '#';

  const handleSave = () => {
    // For now, just close the modal
    onSave && onSave({ invoiceStatus, clientInvoiceNo, internalComments });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Invoice Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Invoice Info */}
          <Box bg="gray.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">Invoice Info</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">Status</Text>
                <Flex align="center" gap={2}>
                  <Badge colorScheme={
                    invoiceStatus === 'Pending' ? 'yellow' :
                    invoiceStatus === 'Draft on Xero' ? 'blue' :
                    invoiceStatus === 'Hold for PO' ? 'orange' :
                    invoiceStatus === 'Sent to Client' ? 'green' : 'gray'
                  } fontSize="sm">{invoiceStatus}</Badge>
                  {!editingStatus && (
                    <IconButton
                      icon={<EditIcon />}
                      aria-label="Edit Status"
                      size="xs"
                      variant="ghost"
                      onClick={() => setEditingStatus(true)}
                    />
                  )}
                </Flex>
                {editingStatus && (
                  <ChakraSelect
                    value={invoiceStatus}
                    onChange={e => {
                      setInvoiceStatus(e.target.value);
                      setEditingStatus(false);
                    }}
                    size="sm"
                    mt={2}
                    onBlur={() => setEditingStatus(false)}
                    autoFocus
                  >
                    <option value="Pending">Pending</option>
                    <option value="Draft on Xero">Draft on Xero</option>
                    <option value="Hold for PO">Hold for PO</option>
                    <option value="Sent to Client">Sent to Client</option>
                  </ChakraSelect>
                )}
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Date Submitted</Text>
                <Text>{invoice.date}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Time Submitted</Text>
                <Text>{timeSubmitted}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Invoice Date</Text>
                <Text>{invoiceDate}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Invoice Number</Text>
                <Text>{invoiceNumber}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Currency</Text>
                <Text>{currency}</Text>
              </Box>
            </SimpleGrid>
          </Box>
          <Divider my={4} />
          {/* Project Info */}
          <Box bg="green.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">Project Info</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">Associate Name</Text>
                <Text>{invoice.associate}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Company Name</Text>
                <Text>{invoice.company}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Client Name</Text>
                <Text>{clientName}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Project Code</Text>
                <Text>{projectCode}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Project End Date</Text>
                <Text>{projectEndDate}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Start Date</Text>
                <Text>{startDate}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Associates</Text>
                <Text>{associates}</Text>
              </Box>
            </SimpleGrid>
          </Box>
          <Divider my={4} />
          {/* Financials */}
          <Box bg="gray.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">Financials</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">Quantity (Days)</Text>
                <Text>{quantity}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Day Rate</Text>
                <Text>£{dayRate}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Billing Type</Text>
                <Text>{invoice.billingType}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Total (No VAT)</Text>
                <Text>£{totalWithoutVAT}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">VAT (20%)</Text>
                <Text>£{vat}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Total (With VAT)</Text>
                <Text>£{totalWithVAT}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Client Charge</Text>
                <Text>£{clientCharge}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Margin per Day</Text>
                <Text>£{marginPerDay}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Client Invoice Value</Text>
                <Text>£{clientInvoiceValue}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Recruitment Cost</Text>
                <Text>£{recruitmentCost}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Deal Margin</Text>
                <Text>{dealMargin}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Brighter Profit</Text>
                <Text>£{brighterProfit}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">OJ Margin (%)</Text>
                <Text>{ojMarginPercent}%</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Total Margin</Text>
                <Text>£{totalMargin}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Brighter Margin</Text>
                <Text>£{brighterMargin}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">OJ Margin</Text>
                <Text>£{ojMargin}</Text>
              </Box>
            </SimpleGrid>
          </Box>
          <Divider my={4} />
          {/* Comments & Links */}
          <Box bg="green.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">Comments & Links</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">Comments</Text>
                <Text>{comments}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">Invoice Link</Text>
                <IconButton
                  as="a"
                  href={invoiceLink}
                  target="_blank"
                  aria-label="View Invoice"
                  icon={<FaFileInvoice />}
                  colorScheme="blue"
                  variant="ghost"
                  size="sm"
                  isDisabled
                />
              </Box>
            </SimpleGrid>
          </Box>
          <Divider my={4} />
          {/* CMAP Details */}
          <Box bg="gray.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">CMAP Details</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box display="flex" alignItems="center" gap={2}>
                <Text fontSize="sm" color="gray.500">CMAP Company ID</Text>
                <Text>{cmapCompanyId}</Text>
                <IconButton
                  as="a"
                  href={cmapCompanyLink}
                  target="_blank"
                  aria-label="View CMAP Company"
                  icon={<ExternalLinkIcon />}
                  colorScheme="blue"
                  variant="ghost"
                  size="sm"
                  ml={1}
                />
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Text fontSize="sm" color="gray.500">CMAP Project ID</Text>
                <Text>{cmapProjectId}</Text>
                <IconButton
                  as="a"
                  href={cmapProjectLink}
                  target="_blank"
                  aria-label="View CMAP Project"
                  icon={<ExternalLinkIcon />}
                  colorScheme="blue"
                  variant="ghost"
                  size="sm"
                  ml={1}
                />
              </Box>
            </SimpleGrid>
          </Box>
          <Divider my={4} />
          {/* Editable Fields */}
          <Box bg="green.50" borderRadius="md" p={4} mb={2}>
            <Text fontWeight="bold" mb={2} color="gray.700">Client Invoice</Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <ChakraFormControl>
                  <FormLabel>Client Invoice No</FormLabel>
                  <Input value={clientInvoiceNo} onChange={e => setClientInvoiceNo(e.target.value)} size="sm" />
                </ChakraFormControl>
              </Box>
              <Box gridColumn={{ md: '1 / span 2' }}>
                <ChakraFormControl>
                  <FormLabel>Internal Comments</FormLabel>
                  <Textarea value={internalComments} onChange={e => setInternalComments(e.target.value)} rows={2} size="sm" />
                </ChakraFormControl>
              </Box>
            </SimpleGrid>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="green" mr={3} onClick={handleSave}>
            Save
          </Button>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DashboardAppBar({ userEmail, onLogout }) {
  return (
    <Flex
      as="header"
      w="100%"
      align="center"
      justify="flex-end"
      px={6}
      py={2}
      bg="gray.50"
      borderBottom="1px solid"
      borderColor="gray.200"
      minH="48px"
      position="sticky"
      top={0}
      zIndex={100}
    >
      <HStack spacing={3}>
        <Text fontWeight="bold" color="green.600" mr={2}>CM</Text>
        <Text fontSize="sm" color="gray.700">{userEmail}</Text>
        <Button colorScheme="red" size="sm" onClick={onLogout}>
          Logout
        </Button>
      </HStack>
    </Flex>
  );
}

function DashboardPage() {
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();
  const [monthFilter, setMonthFilter] = useState('All');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Dummy data for Uploaded Invoices
  const uploadedInvoices = [
    { id: 1, associate: 'Jane Smith', company: 'JS Consulting Ltd', date: '12-06-2024', billingType: 'Day Rate', invoiceUrl: '#' },
    { id: 2, associate: 'John Doe', company: 'Doe Advisory Services', date: '15-06-2024', billingType: 'Day Rate', invoiceUrl: '#' },
    { id: 3, associate: 'Alice Johnson', company: 'A Johnson Solutions', date: '01-05-2024', billingType: 'Fixed Fee', invoiceUrl: '#' },
    { id: 4, associate: 'Bob Lee', company: 'Lee Project Partners', date: '28-04-2024', billingType: 'Day Rate', invoiceUrl: '#' },
  ];

  // Dummy data for Review Required
  const reviewRequired = [
    { id: 1, associate: 'Jane Smith', company: 'JS Consulting Ltd', reason: 'Missing Data', status: 'Stitch Analysis Complete' },
    { id: 2, associate: 'John Doe', company: 'Doe Advisory Services', reason: 'Incorrect Days', status: 'Stitch Analysis Complete' },
    { id: 3, associate: 'Alice Johnson', company: 'A Johnson Solutions', reason: 'Multiple Projects', status: 'No CMAP Project Found' },
    { id: 4, associate: 'Bob Lee', company: 'Lee Project Partners', reason: 'Miscalculation', status: 'Stitch Analysis Complete' },
  ];

  // Month filter options
  const months = ['All', 'June 2024', 'May 2024', 'April 2024'];

  // Filter invoices by month
  const filteredInvoices =
    monthFilter === 'All'
      ? uploadedInvoices
      : uploadedInvoices.filter(inv => {
          const [, month, year] = inv.date.split('-');
          const monthYear =
            (month === '06' ? 'June' : month === '05' ? 'May' : month === '04' ? 'April' : '') +
            ' ' + year;
          return monthYear === monthFilter;
        });

  // Summary metrics
  const totalInvoices = uploadedInvoices.length;
  const totalReview = reviewRequired.length;
  const statusCounts = uploadedInvoices.reduce((acc, inv) => {
    const status = inv.invoiceStatus || 'Pending';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  // Ensure all statuses are present
  const allStatuses = ['Pending', 'Draft on Xero', 'Hold for PO', 'Sent to Client'];
  allStatuses.forEach(status => {
    if (!statusCounts[status]) statusCounts[status] = 0;
  });

  useEffect(() => {
    async function fetchUser() {
      const { data } = await supabase.auth.getUser();
      if (data && data.user) {
        setUserEmail(data.user.email);
      } else {
        navigate('/login');
      }
    }
    fetchUser();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <DashboardAppBar userEmail={userEmail} onLogout={handleLogout} />
      <Container maxW="7xl" py={10} flex="1">
        <BrighterLogoHeader />
        <Box mb={10} />
        {/* Summary Section */}
        <Box mb={10} bg="white" borderRadius="lg" boxShadow="md" p={6}>
          <SimpleGrid columns={6} spacing={4}>
            <Stat textAlign="center">
              <StatLabel>Invoices Uploaded</StatLabel>
              <StatNumber color="green.600"><FaFileInvoice style={{ display: 'inline', marginRight: 8 }} />{totalInvoices}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel>Review Required</StatLabel>
              <StatNumber color="orange.500"><FaExclamationCircle style={{ display: 'inline', marginRight: 8 }} />{totalReview}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel>Pending</StatLabel>
              <StatNumber color="yellow.600"><FaHourglassHalf style={{ display: 'inline', marginRight: 8 }} />{statusCounts['Pending']}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel>Draft on Xero</StatLabel>
              <StatNumber color="blue.600"><FaRegClock style={{ display: 'inline', marginRight: 8 }} />{statusCounts['Draft on Xero']}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel>Hold for PO</StatLabel>
              <StatNumber color="orange.700"><FaExclamationCircle style={{ display: 'inline', marginRight: 8 }} />{statusCounts['Hold for PO']}</StatNumber>
            </Stat>
            <Stat textAlign="center">
              <StatLabel>Sent to Client</StatLabel>
              <StatNumber color="green.700"><FaRegPaperPlane style={{ display: 'inline', marginRight: 8 }} />{statusCounts['Sent to Client']}</StatNumber>
            </Stat>
          </SimpleGrid>
        </Box>
        {/* Uploaded Invoices Section */}
        <Box mb={10} p={6} bg="white" borderRadius="lg" boxShadow="md" width="100%">
          <Flex justify="space-between" align="center" mb={4}>
            <Text fontSize="xl" fontWeight="bold">Uploaded Invoices</Text>
            <Select
              width="200px"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
              bg="gray.50"
              borderColor="gray.200"
              size="sm"
            >
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Select>
          </Flex>
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th>Date Submitted</Th>
                <Th>Associate Name</Th>
                <Th>Company Name</Th>
                <Th>Billing Type</Th>
                <Th textAlign="center">Invoice</Th>
                <Th textAlign="center">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredInvoices.length === 0 ? (
                <Tr>
                  <Td colSpan={6} textAlign="center" color="gray.400">No invoices found for this month.</Td>
                </Tr>
              ) : (
                filteredInvoices.map(inv => (
                  <Tr key={inv.id}>
                    <Td>{inv.date}</Td>
                    <Td>{inv.associate}</Td>
                    <Td>{inv.company}</Td>
                    <Td>{inv.billingType}</Td>
                    <Td textAlign="center">
                      <IconButton
                        as="a"
                        href={inv.invoiceUrl}
                        target="_blank"
                        aria-label="View Invoice"
                        icon={<FaFileInvoice />}
                        colorScheme="blue"
                        variant="ghost"
                        size="sm"
                        isDisabled
                      />
                    </Td>
                    <Td textAlign="center">
                      <Button size="sm" colorScheme="green" variant="outline" onClick={() => { setSelectedInvoice(inv); onOpen(); }}>
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
        {/* Review Required Section */}
        <Box p={6} bg="white" borderRadius="lg" boxShadow="md" width="100%">
          <Text fontSize="xl" fontWeight="bold" mb={4}>Review Required</Text>
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th>Status</Th>
                <Th>Review Reason</Th>
                <Th>Associate Name</Th>
                <Th>Company Name</Th>
                <Th textAlign="center">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {reviewRequired.length === 0 ? (
                <Tr>
                  <Td colSpan={5} textAlign="center" color="gray.400">No items require review.</Td>
                </Tr>
              ) : (
                reviewRequired.map(item => (
                  <Tr key={item.id}>
                    <Td>{item.status}</Td>
                    <Td>{item.reason}</Td>
                    <Td>{item.associate}</Td>
                    <Td>{item.company}</Td>
                    <Td textAlign="center">
                      <Button size="sm" colorScheme="orange" variant="outline" isDisabled>
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>
        {selectedInvoice && (
          <UploadedInvoiceDetailsModal
            isOpen={isOpen}
            onClose={() => { setSelectedInvoice(null); onClose(); }}
            invoice={selectedInvoice}
            onSave={() => { setSelectedInvoice(null); onClose(); }}
          />
        )}
      </Container>
    </Box>
  );
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setIsLoading(false);
    if (error) {
      setError(error.message);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <BrighterLogoHeader />
      <Box p={8} borderWidth="1px" borderRadius="lg" boxShadow="lg" bg="white">
        <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Login</Text>
        <form onSubmit={handleLogin}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </FormControl>
            {error && <Text color="red.500" fontSize="sm">{error}</Text>}
            <Button
              type="submit"
              colorScheme="green"
              isLoading={isLoading}
              loadingText="Logging in..."
              width="full"
            >
              Login
            </Button>
            <ChakraLink href="/forgot-password" color="green.600" textAlign="center">
              Forgot your password?
            </ChakraLink>
          </VStack>
        </form>
      </Box>
    </Container>
  );
}

function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    const { error } = await supabase.auth.signUp({ email, password });
    setIsLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Registration successful! Please check your email to confirm your account.');
      setTimeout(() => navigate('/login'), 2000);
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <BrighterLogoHeader />
      <Box p={8} borderWidth="1px" borderRadius="lg" boxShadow="lg" bg="white">
        <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Register</Text>
        <form onSubmit={handleRegister}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </FormControl>
            {error && <Text color="red.500" fontSize="sm">{error}</Text>}
            {success && <Text color="green.600" fontSize="sm">{success}</Text>}
            <Button
              type="submit"
              colorScheme="green"
              isLoading={isLoading}
              loadingText="Registering..."
              width="full"
            >
              Register
            </Button>
            <ChakraLink href="/login" color="green.600" textAlign="center">
              Already have an account? Login
            </ChakraLink>
          </VStack>
        </form>
      </Box>
    </Container>
  );
}

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    setIsLoading(false);
    if (error) {
      setError(error.message);
    } else {
      setSuccess('If this email is registered, a password reset link has been sent. Please check your inbox.');
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <BrighterLogoHeader />
      <Box p={8} borderWidth="1px" borderRadius="lg" boxShadow="lg" bg="white">
        <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Forgot Password</Text>
        <form onSubmit={handleForgotPassword}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </FormControl>
            {error && <Text color="red.500" fontSize="sm">{error}</Text>}
            {success && <Text color="green.600" fontSize="sm">{success}</Text>}
            <Button
              type="submit"
              colorScheme="green"
              isLoading={isLoading}
              loadingText="Sending..."
              width="full"
            >
              Send Password Reset Email
            </Button>
            <ChakraLink href="/login" color="green.600" textAlign="center">
              Back to Login
            </ChakraLink>
          </VStack>
        </form>
      </Box>
    </Container>
  );
}

function CheckPage() {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === 'Stitch') {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password.');
    }
  };

  const handleCheck = async () => {
    setIsLoading(true);
    setResponse(null);
    setError('');
    try {
      const res = await fetch('https://n8n.alba-nova.com/webhook/check', { method: 'POST' });
      const data = await res.json();
      setResponse(data);
    } catch (err) {
      setError('Failed to check invoice submissions.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={10}>
      <BrighterLogoHeader />
      <Box p={8} borderWidth="1px" borderRadius="lg" boxShadow="lg" bg="white">
        <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center" display={response ? 'none' : undefined}>Check Invoice Submissions</Text>
        {!isAuthenticated ? (
          <form onSubmit={handlePasswordSubmit}>
            <FormControl isRequired mb={4}>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoFocus
                />
                <InputRightElement width="4.5rem">
                  <Button h="1.75rem" size="sm" type="submit" colorScheme="green">Enter</Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            {error && <Text color="red.500" fontSize="sm" mb={2}>{error}</Text>}
          </form>
        ) : (
          <>
            <Button
              colorScheme="green"
              size="lg"
              width="full"
              mb={6}
              onClick={handleCheck}
              isLoading={isLoading}
              loadingText="Checking Associate Invoices"
              leftIcon={isLoading ? <Spinner size="sm" /> : null}
              disabled={isLoading}
              display={response ? 'none' : undefined}
            >
              Check Invoice Submissions
            </Button>
            {error && <Text color="red.500" fontSize="sm" mb={2}>{error}</Text>}
            {response && (
              <Box mt={6} p={4} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" mb={2} fontSize="lg" textAlign="center">Associates Without Invoice Submission</Text>
                {(() => {
                  let namesArr = [];
                  if (Array.isArray(response)) {
                    if (typeof response[0] === 'string') {
                      namesArr = response;
                    } else if (response[0]?.names) {
                      namesArr = response[0].names;
                    }
                  }
                  const filtered = namesArr
                    .filter(n => n.trim() && n.trim() !== 'stop')
                    .filter(n => n.toLowerCase().includes(search.toLowerCase()));
                  return (
                    <Text mb={4} color="gray.600" fontSize="md" textAlign="center">
                      Showing <b>{filtered.length}</b> associate{filtered.length !== 1 ? 's' : ''} without invoice submission
                    </Text>
                  );
                })()}
                <Input
                  placeholder="Search associate name..."
                  mb={4}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  bg="white"
                />
                <Table variant="simple" size="md" mb={4}>
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>Name</Th>
                      <Th>Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(() => {
                      let namesArr = [];
                      if (Array.isArray(response)) {
                        if (typeof response[0] === 'string') {
                          // Response is a plain array of names
                          namesArr = response;
                        } else if (response[0]?.names) {
                          // Response is an array of objects with a names property
                          namesArr = response[0].names;
                        }
                      }
                      return namesArr
                        .filter(n => n.trim() && n.trim() !== 'stop')
                        .filter(n => n.toLowerCase().includes(search.toLowerCase()))
                        .map((name, idx) => (
                          <Tr key={idx}>
                            <Td>{name}</Td>
                            <Td><Badge colorScheme="red">Invoice Not Yet Submitted</Badge></Td>
                          </Tr>
                        ));
                    })()}
                  </Tbody>
                </Table>
                {response[0]?.notes && (
                  <Box mt={4} p={3} bg="yellow.50" borderRadius="md" border="1px solid #F6E05E">
                    <Text fontWeight="semibold" color="yellow.800">Note:</Text>
                    <Text color="yellow.800" fontSize="sm">{response[0].notes}</Text>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

function Footer() {
  return (
    <Box as="footer" w="100%" bg="gray.50" borderTop="1px" borderColor="gray.200" py={4} px={2} fontSize="sm" color="gray.600" textAlign="center">
      <Box mb={2}>
        © Copyright 2025 Brighter Consultancy Group<br />
        Brighter Consultancy Group Ltd, a company incorporated and registered in Scotland<br />
        (Company number SC682647)
      </Box>
      <Divider my={3} />
      <HStack spacing={3} justify="center" fontSize="xs">
        <ChakraLink href="https://brighterconsultancy.com/contact" isExternal>Contact Us</ChakraLink>
        <span>|</span>
        <ChakraLink href="https://brighterconsultancy.com/privacy-policy" isExternal>Privacy Policy</ChakraLink>
        <span>|</span>
        <ChakraLink href="https://brighterconsultancy.com/diversity-inclusion-policy" isExternal>Diversity & Inclusion Policy</ChakraLink>
        <span>|</span>
        <ChakraLink href="https://brighterconsultancy.com/carbon-reduction-policy" isExternal>Carbon Reduction Policy</ChakraLink>
        <span>|</span>
        <ChakraLink href="https://www.linkedin.com/company/brighter-consultancy-limited/" isExternal aria-label="LinkedIn">
          <Icon as={FaLinkedin} boxSize={4} verticalAlign="middle" />
        </ChakraLink>
      </HStack>
    </Box>
  );
}

const PORTAL_URL = 'https://portal.brighterconsultancy.com';

function PortalRedirect() {
  return (
    <Box
      as="a"
      href={PORTAL_URL}
      aria-label="Go to the Brighter Portal"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minH="100vh"
      w="100%"
      bg="black"
      cursor="pointer"
    >
      <Image
        src={`${process.env.PUBLIC_URL}/redirect.png`}
        alt="This link is no longer active. All invoices should now be submitted through the new Brighter Portal."
        maxW="100%"
        maxH="100vh"
        w="100%"
        h="100vh"
        objectFit="contain"
      />
    </Box>
  );
}

function App() {
  return (
    <Routes>
      <Route path="*" element={<PortalRedirect />} />
    </Routes>
  );
}

function LegacyInvoiceApp() {
  return (
    <Flex minHeight="100vh" direction="column">
      <Box flex="1">
        <Routes>
          <Route path="/" element={<InvoiceUpload />} />
          <Route path="/sandbox" element={<InvoiceUploadSandbox />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/check" element={<CheckPage />} />
        </Routes>
      </Box>
      <Footer />
    </Flex>
  );
}

export { LegacyInvoiceApp };

export default App; 